import { useCallback, useEffect, useState } from "react";
import {
  API_ROUTES,
  type Comment,
  type CommentSort,
  type Paginated,
  type VoteCommentResponse,
} from "@ziolo/shared";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useSession } from "../session/SessionProvider.js";

/** The legacy hub rendered every comment at once; this pages instead. */
const PAGE_SIZE = 25;

export interface CommentsState {
  items: Comment[];
  total: number;
  sort: CommentSort;
  setSort: (sort: CommentSort) => void;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  /** Places a freshly created comment without a full refetch where possible. */
  add: (comment: Comment) => Promise<void>;
  vote: (id: number, vote: 1 | -1) => Promise<void>;
  remove: (id: number) => Promise<void>;
}

function listQuery(sort: CommentSort, page: number): string {
  const query = new URLSearchParams({
    sort,
    page: String(page),
    pageSize: String(PAGE_SIZE),
  });
  return `${API_ROUTES.comments.list}?${query.toString()}`;
}

export function useComments(): CommentsState {
  const { user } = useSession();
  const [sort, setSort] = useState<CommentSort>("new");
  const [items, setItems] = useState<Comment[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refetched when the signed-in user changes: `userVote` and `canDelete` are answers
  // about the caller, so a stale list would show someone else's arrows and buttons.
  const viewerId = user?.id ?? null;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    apiRequest<Paginated<Comment>>(listQuery(sort, 1), { signal: controller.signal })
      .then((result) => {
        setItems(result.items);
        setTotal(result.total);
        setPage(1);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(errorMessage(cause));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [sort, viewerId]);

  const reload = useCallback(async () => {
    const result = await apiRequest<Paginated<Comment>>(listQuery(sort, 1));
    setItems(result.items);
    setTotal(result.total);
    setPage(1);
  }, [sort]);

  const loadMore = useCallback(async () => {
    setLoadingMore(true);
    try {
      const next = page + 1;
      const result = await apiRequest<Paginated<Comment>>(listQuery(sort, next));
      // Filter by id: a comment added or deleted since the first page loaded would
      // otherwise appear twice as the offset shifts under us.
      setItems((current) => {
        const seen = new Set(current.map((comment) => comment.id));
        return [...current, ...result.items.filter((item) => !seen.has(item.id))];
      });
      setTotal(result.total);
      setPage(next);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setLoadingMore(false);
    }
  }, [page, sort]);

  const add = useCallback(
    async (comment: Comment) => {
      if (sort === "new") {
        setItems((current) => [comment, ...current]);
        setTotal((current) => current + 1);
        return;
      }
      // Under any other sort the new comment's place is not the top, so ask the server.
      await reload();
    },
    [sort, reload],
  );

  const vote = useCallback(async (id: number, value: 1 | -1) => {
    const tally = await apiRequest<VoteCommentResponse>(API_ROUTES.comments.vote(id), {
      method: "POST",
      body: { vote: value },
    });

    setItems((current) =>
      current.map((comment) => (comment.id === id ? { ...comment, ...tally } : comment)),
    );
  }, []);

  const remove = useCallback(async (id: number) => {
    await apiRequest(API_ROUTES.comments.remove(id), { method: "DELETE" });
    setItems((current) => current.filter((comment) => comment.id !== id));
    setTotal((current) => Math.max(0, current - 1));
  }, []);

  return {
    items,
    total,
    sort,
    setSort,
    loading,
    loadingMore,
    error,
    hasMore: items.length < total,
    loadMore,
    add,
    vote,
    remove,
  };
}
